import type {
  DocumentMetadataItem,
  EFilingAttorney,
  EFilingParty,
  EFilingType,
} from '@lds-efsp/shared-contracts';
import {
  getAssignmentProvenance,
  type AssignmentProvenanceMap,
  type PartyClassCode,
} from '@lds-efsp/shared-contracts';

/** Plaintiff-side roles for auto-populating FILING_PARTY (single source —
 *  previously duplicated across DocumentsTab's auto-derive and its roster
 *  backstop effect; both inline copies are deleted — they consult the guarded
 *  builder below instead). */
export const PLAINTIFF_ROLES = ['PLAIN', 'PET', 'CLAIM', 'XCMPLNT', 'XPLAIN', 'INTPLAIN', 'INTVEN', 'INTPLD'];

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * The one deterministic Filing Attorney choice for a set of filing-party ids
 * (Walter 2026-07-10 R20): the single roster attorney linked to a filing party
 * (`representedPartyIds`), else the sole roster attorney. Null when ambiguous
 * (two candidates), when the roster is empty, or when a filing party is
 * self-represented — empty FILING_ATTORNEY is the CORRECT value for self-rep
 * (R18/R19; the envelope emits FILING_PARTY_ADDRESS instead), so auto-fill
 * must never paper over it. On a subsequent filing, extraction-sourced
 * attorneys are never offered (R6), so never auto-picked; on a case-initiation
 * the caller sets `offerExtractionEntries` (Walter R81/R83) so the sole
 * OCR-read attorney auto-assigns like any other — the auto-pick pool must
 * match what the selectors offer (DocumentMetadataForm), or R58's auto-assign
 * silently skips exactly the runs where extraction did its job.
 */
export function pickUnambiguousFilingAttorney(
  filingPartyIds: string[],
  parties: EFilingParty[],
  attorneys: EFilingAttorney[],
  offerExtractionEntries?: boolean,
): EFilingAttorney | null {
  const anySelfRep = filingPartyIds.some(
    id => parties.find(p => p.id === id)?.isSelfRepresented === true,
  );
  if (anySelfRep) return null;
  const offered = offerExtractionEntries
    ? attorneys
    : attorneys.filter(a => a.source !== 'extraction');
  const linked = offered.filter(a => a.representedPartyIds?.some(id => filingPartyIds.includes(id)));
  if (linked.length === 1) return linked[0];
  if (linked.length === 0 && offered.length === 1) return offered[0];
  return null;
}

/**
 * Whether the draft's roster is sourced from the attached case's own court
 * record (SPEC-SOR-220-v2 R6): the filing is a subsequent filing AND some
 * roster party carries the `case-roster` tag. On that path the filing party is
 * always the filer's own choice — on fresh attach and after a case change
 * alike — so the plaintiff-role FILING_PARTY fill must not mint one.
 *
 * The TWO conjuncts are both load-bearing, and `some` over `case-roster` is
 * deliberately monotone under the post-attach extraction append (SOR-223
 * appends genuinely-new entries; case-roster members survive it) — the
 * fail-closed property the guard needs: an extraction entry landing after
 * attach cannot unlock the fill. An untagged/manual/extraction-only roster is
 * NOT court-sourced, which is what keeps the unattached-lookup state and the
 * OCR-minted rosters filling exactly as before.
 */
export function isCourtSourcedRoster(args: {
  filingType: EFilingType | '';
  parties: EFilingParty[];
}): boolean {
  return (
    args.filingType === 'subsequent-filing' &&
    args.parties.some(p => p.source === 'case-roster')
  );
}

/**
 * Gap-fill patch for one document's participant assignments (Walter R20).
 * Participants are created on About Your Case, but the per-doc references live
 * in `metadataValues` written at doc-typing time on Upload — so whichever step
 * the filer is on when the roster lands must be able to fill the gaps without
 * a walk back. Fill-only: an existing selection is never overwritten.
 *
 * - FILING_PARTY: plaintiff-side parties, when the policy item exists and the
 *   value is empty (mirrors DocumentsTab's roster backstop).
 * - REFERS_TO_PARTIES: non-plaintiff parties, when the synthetic key is
 *   already present (seeded at doc-typing) and empty.
 * - FILING_ATTORNEY: the unambiguous pick, when the policy item exists and
 *   the value is empty (never for a self-rep filing party).
 * - FORMER_ATTORNEY (SPEC-SOR-258-v1 R4/R5): the sole court-sourced attorney,
 *   when the policy item exists and the value is empty — never for a cleared
 *   or filer-chosen slot, never with zero or two-plus candidates.
 * - PARTY_SERVED: non-plaintiff parties, when the policy item exists and the
 *   value is empty.
 *
 * Guarded (SPEC-SOR-220-v2 R5/R6) — the ONE decider for which parties fill an
 * empty FILING_PARTY, in the guided and classic flows alike. It refuses two
 * cases a naked roster look would accept:
 *
 * - a slot whose assignment provenance reads `cleared` is never written — a
 *   case change dropped that reference, and only the filer's own act resolves
 *   it. Provenance resolves exclusively through `getAssignmentProvenance`, so
 *   a legacy untagged draft keeps its canonical readings (a held reference ⇒
 *   `filer_chosen`, empty ⇒ `untouched`).
 * - FILING_PARTY is never filled from plaintiff roles while
 *   `courtSourcedRoster` holds — on the court's own record the filing party
 *   may be the defendant, and an auto-fill is how a defendant was re-minted
 *   as a plaintiff.
 *
 * The builder stays fill-only and pure: it returns a patch, it never writes —
 * callers record what it filled through the context writer.
 *
 * Returns only the keys that change; empty object = nothing to fill.
 */
export function buildAssignmentFillPatch(args: {
  metadataValues: Record<string, unknown>;
  metadataItems: DocumentMetadataItem[];
  parties: EFilingParty[];
  attorneys: EFilingAttorney[];
  /** The document's recorded per-slot provenance (sparse; absence is resolved
   *  by the ONE reader, never by hand). */
  assignmentProvenance?: AssignmentProvenanceMap;
  /** Roster origin — the verdict of `isCourtSourcedRoster`, stated by the
   *  caller rather than defaulted: a defaulted boolean silently picks a side
   *  for the next call site that forgets. */
  courtSourcedRoster: boolean;
  /** Offer extraction-sourced entries to the attorney auto-pick (R81/R83 —
   *  case-initiation only; see pickUnambiguousFilingAttorney). */
  offerExtractionEntries?: boolean;
}): Record<string, unknown> {
  const {
    metadataValues: mv,
    metadataItems,
    parties,
    attorneys,
    offerExtractionEntries,
    assignmentProvenance,
    courtSourcedRoster,
  } = args;
  const patch: Record<string, unknown> = {};

  // The slot's provenance, through the ONE absence-resolving reader.
  const provenanceOf = (code: PartyClassCode) =>
    getAssignmentProvenance({ metadataValues: mv, assignmentProvenance }, code);

  const hasFilingParty = metadataItems.some(
    m => m.code === 'FILING_PARTY' && m.valueRestriction !== 'new-data',
  );
  let filingIds = asStringArray(mv.FILING_PARTY);
  if (
    hasFilingParty &&
    filingIds.length === 0 &&
    provenanceOf('FILING_PARTY') !== 'cleared' &&
    !courtSourcedRoster
  ) {
    const plaintiffIds = parties.filter(p => PLAINTIFF_ROLES.includes(p.roleCode)).map(p => p.id);
    if (plaintiffIds.length > 0) {
      patch.FILING_PARTY = plaintiffIds;
      filingIds = plaintiffIds;
    }
  }

  if (
    'REFERS_TO_PARTIES' in mv &&
    asStringArray(mv.REFERS_TO_PARTIES).length === 0 &&
    provenanceOf('REFERS_TO_PARTIES') !== 'cleared'
  ) {
    const nonFilingIds = parties.filter(p => !PLAINTIFF_ROLES.includes(p.roleCode)).map(p => p.id);
    if (nonFilingIds.length > 0) {
      patch.REFERS_TO_PARTIES = nonFilingIds;
    }
  }

  const hasFilingAttorney = metadataItems.some(
    m => m.code === 'FILING_ATTORNEY' && m.valueRestriction !== 'new-data',
  );
  if (
    hasFilingAttorney &&
    asStringArray(mv.FILING_ATTORNEY).length === 0 &&
    provenanceOf('FILING_ATTORNEY') !== 'cleared'
  ) {
    const pick = pickUnambiguousFilingAttorney(filingIds, parties, attorneys, offerExtractionEntries);
    if (pick) {
      patch.FILING_ATTORNEY = [pick.id];
    }
  }

  // SPEC-SOR-258-v1 R4/R5 (the rule DocumentsTab's doc-typing seed ships —
  // moved here so the ONE decider owns it; the inline copy goes when the
  // cutover lands). The former attorney is the one being TAKEN OFF the case,
  // so the fill only follows from a filer action when the case roster leaves
  // no choice: exactly ONE court-sourced attorney (externalId = court
  // participant id — a hand-added attorney is never a candidate, SOR-151).
  // Zero or two-plus candidates fill nothing and nothing is preselected. The
  // seed is additionally refused a slot the filer personally chose — unlike
  // the sibling legs, `filer_chosen` is a refusal here too (R5). WOL-344:
  // store as string[] so getPartyIds (stage-3-envelope.ts) can unwrap it —
  // a bare string is silently ignored at submit.
  const hasFormerAttorney = metadataItems.some(
    m => m.code === 'FORMER_ATTORNEY' && m.valueRestriction !== 'new-data',
  );
  if (
    hasFormerAttorney &&
    asStringArray(mv.FORMER_ATTORNEY).length === 0 &&
    provenanceOf('FORMER_ATTORNEY') !== 'cleared' &&
    provenanceOf('FORMER_ATTORNEY') !== 'filer_chosen'
  ) {
    const caseAttorneys = attorneys.filter(a => a.externalId);
    if (caseAttorneys.length === 1) {
      patch.FORMER_ATTORNEY = [caseAttorneys[0].id];
    }
  }

  // Seed leg for PARTY_SERVED — the non-plaintiff parties (the same fill the
  // doc-typing seed derives), so the builder can supply everything that seed
  // writes today. Policy-exposure-gated like its siblings, never
  // key-presence-gated: a synthetic empty key must not arm a fill.
  const hasPartyServed = metadataItems.some(
    m => m.code === 'PARTY_SERVED' && m.valueRestriction !== 'new-data',
  );
  if (
    hasPartyServed &&
    asStringArray(mv.PARTY_SERVED).length === 0 &&
    provenanceOf('PARTY_SERVED') !== 'cleared'
  ) {
    const nonPlaintiffIds = parties.filter(p => !PLAINTIFF_ROLES.includes(p.roleCode)).map(p => p.id);
    if (nonPlaintiffIds.length > 0) {
      patch.PARTY_SERVED = nonPlaintiffIds;
    }
  }

  return patch;
}
